import type { IQService } from 'angular';
import angular from 'angular';
import type { ApiService } from '../api/types';
import type { LimitsValidationResponse, LimitsViolation } from './limits-validation.types';
import type { OrderUpdateInput } from '../api/ordersApi/types/api/calculate.type';

export interface LimitsValidationCallbacks {
  onSuccess: (messages: Array<{ message: string }>) => void;
  onError: (messages: Array<{ message: string }>) => void;
  hideMessages: () => void;
}

export class OrderEditSalesRulesService {
  static $inject = ['$q', 'ApiService'];

  /** Limits violations per event, keyed by eventId */
  private violations: Record<string, LimitsViolation[]> = {};

  /** Whether limits override is active, keyed by eventId */
  private overrides: Record<string, boolean> = {};

  /** Whether limits have been checked, keyed by eventId */
  private checkedEvents: Record<string, boolean> = {};

  constructor(
    private $q: IQService,
    private ApiService: ApiService,
  ) {}

  /**
   * Validates limits for an order edit operation
   * @param orderData - The order update input data to validate (OrderUpdateInput)
   * @param callbacks - Callback functions for handling success/error states
   * @returns Promise that resolves with the validation response
   */
  validateLimits(orderData: OrderUpdateInput, callbacks: LimitsValidationCallbacks): angular.IPromise<LimitsValidationResponse> {
    const deferred = this.$q.defer<LimitsValidationResponse>();

    callbacks.hideMessages();

    this.ApiService.orders
      .validateEntitlements(orderData)
      .then((response) => {
        const validationResponse = response.data.payload as LimitsValidationResponse;
        this.processValidationResponse(validationResponse, callbacks);
        deferred.resolve(validationResponse);
      })
      .catch((error) => {
        callbacks.onError([{ message: error.data?.error || 'Failed to validate limits' }]);
        deferred.reject(error);
      });

    return deferred.promise;
  }

  /**
   * Processes the validation response and triggers appropriate callbacks
   */
  processValidationResponse(response: LimitsValidationResponse, callbacks: LimitsValidationCallbacks): void {
    // Handle empty response
    if (!response.results || response.results.length === 0) {
      callbacks.onSuccess([{ message: 'All ticket limits verified successfully' }]);
      return;
    }

    // Handle validation errors
    if (!response.valid) {
      if (response.messages && response.messages.length > 0) {
        const errorMessages = response.messages.map((msg) => ({ message: msg }));
        console.log('Limits validation error:', errorMessages);
        callbacks.onError([{ message: 'Selected tickets are not within limits' }]);
      }

      // Log violations for debugging
      if (response.violations && response.violations.length > 0) {
        console.warn('Limits violations:', response.violations);

        response.violations.forEach((violation) => {
          const violationMsg = `Event ${violation.eventId}: Requested ${violation.requestedQuantity}, ` +
            `but only ${violation.allowedQuantity - violation.purchasedQuantity} remaining ` +
            `(${violation.purchasedQuantity} already purchased)`;
          console.warn(violationMsg);
        });
      }

      return;
    }

    // Handle successful validation
    if (response.valid && response.passed && response.passed.length > 0) {
      callbacks.onSuccess([{ message: 'Member ticket limit: Selected tickets are within limits' }]);

      // Log passed validations for debugging
      console.log('Limits validation passed:', response.passed);

      response.passed.forEach((passed) => {
        const remaining = passed.allowedQuantity - passed.purchasedQuantity;
        console.log(
          `Event ${passed.eventId}: ${remaining} tickets remaining ` +
          `(${passed.purchasedQuantity} purchased, ${passed.allowedQuantity} allowed)`
        );
      });
    } else {
      callbacks.onSuccess([{ message: 'Member ticket limit: Selected tickets are within limits' }]);
    }
  }

  /**
   * Stores violations from a validation response, grouped by eventId
   * Also marks all events in the response as checked
   */
  storeViolations(response: LimitsValidationResponse): void {
    this.violations = {};

    // Mark events with violations as checked
    if (response.violations && response.violations.length > 0) {
      for (const violation of response.violations) {
        if (!this.violations[violation.eventId]) {
          this.violations[violation.eventId] = [];
        }
        this.violations[violation.eventId].push(violation);
        this.checkedEvents[violation.eventId] = true;
      }
    }

    // Mark events that passed as checked
    if (response.passed && response.passed.length > 0) {
      for (const passed of response.passed) {
        this.checkedEvents[passed.eventId] = true;
      }
    }
  }

  /**
   * Clears all stored violations
   */
  clearViolations(): void {
    this.violations = {};
  }

  /**
   * Gets violations for a specific event
   */
  getViolationsForEvent(eventId: string): LimitsViolation[] | undefined {
    return this.violations[eventId];
  }

  /**
   * Checks if there are violations for a specific event
   */
  hasViolationsForEvent(eventId: string): boolean {
    const violations = this.violations[eventId];
    return Array.isArray(violations) && violations.length > 0;
  }

  /**
   * Toggles the override state for a specific event
   */
  toggleOverride(eventId: string): void {
    this.overrides[eventId] = !this.overrides[eventId];
  }

  /**
   * Checks if override is active for a specific event
   */
  isOverrideActive(eventId: string): boolean {
    return !!this.overrides[eventId];
  }

  /**
   * Clears all override states
   */
  clearOverrides(): void {
    this.overrides = {};
  }

  /**
   * Checks if limits have been checked for a specific event
   */
  isLimitsChecked(eventId: string): boolean {
    return !!this.checkedEvents[eventId];
  }

  /**
   * Clears all checked states
   */
  clearCheckedEvents(): void {
    this.checkedEvents = {};
  }

  /**
   * Checks if all events with violations have overrides active
   * Returns true if there are no violations OR all violations have overrides
   */
  allViolationsHaveOverrides(): boolean {
    const eventIdsWithViolations = Object.keys(this.violations);

    if (eventIdsWithViolations.length === 0) {
      return true;
    }

    return eventIdsWithViolations.every((eventId) => this.isOverrideActive(eventId));
  }
}

export const OrderEditSalesRulesServiceModule = angular
  .module('acme.common.service.orderEditSalesRules', [
    'acme.common.service.api',
  ])
  .service('OrderEditSalesRulesService', OrderEditSalesRulesService).name;
